import { Stack, Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { ColDef, IRowNode } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { IExtractorVendorMapping } from 'companySettings/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextLine, Tooltip } from 'core/elements';
import { Color, Direction, Level } from 'theme/enums';

import { isContactUnresolved } from '../utils';

interface IProps {
  colDef?: ColDef;
  data?: IExtractorVendorMapping;
  node?: IRowNode<IExtractorVendorMapping>;
}

export const VendorContactCell = ({ colDef, data, node }: IProps) => {
  const { t } = useTranslation();

  const contact = data?.vendorContact;

  // Grouping by Vendor Contact makes AG Grid reuse this renderer inside the auto group column, on a
  // row with no data — without the branch below that row would show only its count.
  //
  // The gate is this CELL's colDef, not the row: under `groupDisplayType: 'multipleColumns'`
  // (core/utils/agGridTable.ts) every ordinary column also renders on every group row with no data,
  // where `node.key` belongs to whichever column grouped it — so a grid grouped by Vendor would
  // otherwise repeat the vendor name here. `showRowGroup` sits only on an auto group column's
  // colDef, naming the column it stands in for.
  //
  // The node KEY is the contact name `keyCreator` returns; the value on a group row would be the
  // grouped column's own value from one leaf — an email.
  if (!data) {
    return 'vendorContact' === colDef?.showRowGroup && node?.key ? (
      <TextLine text={node.key} textEllipsis />
    ) : null;
  }

  // An extractor can be mapped with no contact at all, and a blank cell reads as a rendering bug.
  // Wrapped like the two-line path so the dash stays left-aligned — see VendorCell.
  if (!contact) {
    return (
      <Stack>
        <TextLine text={DEFAULT_PLACEHOLDER} textEllipsis />
      </Stack>
    );
  }

  const unresolvedMessage = t('messageExtractorVendorMappingContactUnresolved');

  return (
    <Stack>
      <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5 }}>
        <TextLine text={contact.name} textEllipsis />

        {isContactUnresolved(contact) && (
          <Tooltip title={unresolvedMessage} color={Color.WARNING}>
            <Typography color={Color.WARNING} level={Level.BODY_SM} aria-label={unresolvedMessage}>
              <Warning />
            </Typography>
          </Tooltip>
        )}
      </Stack>

      {contact.email && <TextLine text={contact.email} isSubText textEllipsis />}
    </Stack>
  );
};
